import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Mail, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { chatApi } from '../../services/cloudApi';

const AddFriendModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('chat');
  const { authToken } = useAuth();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const getErrorMessage = (errorCode) => {
    const messages = {
      EMAIL_REQUIRED: t('addFriendModal.errors.emailRequired'),
      USER_NOT_FOUND: t('addFriendModal.errors.userNotFound'),
      CANNOT_ADD_SELF: t('addFriendModal.errors.cannotAddSelf'),
      REQUEST_ALREADY_EXISTS: t('addFriendModal.errors.requestAlreadyExists'),
      ALREADY_FRIENDS: t('addFriendModal.errors.alreadyFriends'),
    };
    return messages[errorCode] || t('addFriendModal.errors.generic');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (!email.trim()) { setError(t('addFriendModal.errors.emailRequired')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError(t('addFriendModal.errors.invalidEmail')); return; }
    setIsLoading(true);
    try {
      const response = await chatApi.sendFriendRequest({ token: authToken, receiverEmail: email.trim() });
      if (response.ok) {
        setSuccess(true);
        setEmail('');
        setTimeout(() => { onClose(); setSuccess(false); setError(null); }, 1500);
      } else {
        setError(getErrorMessage(response.error));
      }
    } catch (err) {
      setError(getErrorMessage(err.payload?.error || err.message));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) { setEmail(''); setError(null); setSuccess(false); onClose(); }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
            onClick={handleClose}
          />

          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[100001] p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 8 }}
              transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
              className="w-full max-w-sm bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between p-6 pb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#2563eb]/10 border border-[#3d8ef0]/20 flex items-center justify-center flex-shrink-0">
                    <UserPlus className="w-5 h-5 text-[#3d8ef0]" strokeWidth={2} />
                  </div>
                  <h2 className="text-base font-semibold text-[var(--color-text-primary)]">{t('addFriendModal.title')}</h2>
                </div>
                <button
                  onClick={handleClose}
                  disabled={isLoading}
                  className="p-1.5 rounded-lg text-[#4a6380] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.06] transition-colors disabled:opacity-50"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 pb-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-[var(--color-text-muted)] mb-2">
                      {t('addFriendModal.emailLabel')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4a6380]" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(null); }}
                        placeholder={t('addFriendModal.emailPlaceholder')}
                        disabled={isLoading || success}
                        autoFocus
                        className="w-full pl-9 pr-4 py-2.5 bg-[var(--color-bg-muted)] border border-white/[0.07] rounded-xl text-sm text-[var(--color-text-primary)] placeholder-[#4a6380] focus:outline-none focus:border-[#3d8ef0]/50 focus:ring-1 focus:ring-[#3d8ef0]/30 disabled:opacity-50 transition-all"
                      />
                    </div>
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-3 bg-red-500/[0.08] border border-red-500/20 rounded-xl text-red-400 text-xs"
                    >
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{error}</span>
                    </motion.div>
                  )}

                  {success && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 p-3 bg-emerald-500/[0.08] border border-emerald-500/20 rounded-xl text-emerald-400 text-xs"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{t('addFriendModal.requestSent')}</span>
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || success || !email.trim()}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold bg-[#2563eb] hover:bg-[#3d8ef0] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>{t('addFriendModal.sending')}</span></>
                    ) : success ? (
                      <><CheckCircle2 className="w-4 h-4" /><span>{t('addFriendModal.sent')}</span></>
                    ) : (
                      t('addFriendModal.sendInvite')
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddFriendModal;
